import { useState, useCallback } from 'react';
import {
  Button, Typography, Tag, Space, Table, Descriptions, message,
  Form, Input, InputNumber, Select, DatePicker,
  Modal, Divider, Flex,
} from 'antd';
import {
  PlusOutlined, SendOutlined, CheckOutlined, CloseOutlined,
  FileTextOutlined, UnorderedListOutlined, DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnDef } from '@shared/types';
import { DISPLAY_DATE_FORMAT, BASE_CURRENCY_CODE } from '@shared/constants';
import { DataTable, DetailDrawer } from '../../components/shared';
import type { FetchParams } from '../../components/shared';
import {
  fetchMRs, getMR, createMR, submitMR, approveMRLines,
  type MRHeader, type MRDetail,
} from '../../api/purchasing';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// ── Status config ──

const MR_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'default' },
  submitted: { label: 'Submitted', color: 'processing' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  partially_approved: { label: 'Partially Approved', color: 'warning' },
  partially_ordered: { label: 'Partially Ordered', color: 'purple' },
  ordered: { label: 'Ordered', color: 'cyan' },
  received: { label: 'Received', color: 'geekblue' },
  closed: { label: 'Closed', color: 'default' },
};

const URGENCY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: 'Low', color: 'default' },
  normal: { label: 'Normal', color: 'blue' },
  high: { label: 'High', color: 'orange' },
  critical: { label: 'Critical', color: 'red' },
};

const LINE_APPROVAL_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'default' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
};

// ── Column Definitions ──

const columns: ColumnDef<MRHeader>[] = [
  { key: 'mrCode', label: 'MR #', dataType: 'string', width: 130 },
  {
    key: 'status', label: 'Status', dataType: 'enum', width: 150,
    options: Object.entries(MR_STATUS_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label, color: cfg.color })),
    render: (v) => {
      const cfg = MR_STATUS_CONFIG[v as string] || { label: v, color: 'default' };
      return <Tag color={cfg.color}>{cfg.label}</Tag>;
    },
  },
  {
    key: 'urgency', label: 'Urgency', dataType: 'enum', width: 110,
    options: Object.entries(URGENCY_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label, color: cfg.color })),
    render: (v) => {
      const cfg = URGENCY_CONFIG[v as string] || { label: v, color: 'default' };
      return <Tag color={cfg.color}>{cfg.label}</Tag>;
    },
  },
  { key: 'lineCount', label: 'Lines', dataType: 'number', width: 80 },
  { key: 'mrTotal', label: 'Total', dataType: 'currency', width: 140 },
  { key: 'requestDate', label: 'Request Date', dataType: 'date', width: 130 },
  { key: 'neededBy', label: 'Needed By', dataType: 'date', width: 130 },
  { key: 'createdAt', label: 'Created', dataType: 'date', width: 130 },
];

// ── Line Table Columns ──

const lineColumns = [
  { title: '#', dataIndex: 'lineNo', key: 'lineNo', width: 50 },
  { title: 'Item Description', dataIndex: 'itemDescription', key: 'itemDescription', width: 200 },
  { title: 'Part #', dataIndex: 'partNumber', key: 'partNumber', width: 100 },
  { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 80, align: 'right' as const },
  {
    title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', width: 120, align: 'right' as const,
    render: (v: number) => `${BASE_CURRENCY_CODE} ${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
  },
  {
    title: 'Line Total', dataIndex: 'lineTotal', key: 'lineTotal', width: 130, align: 'right' as const,
    render: (v: number) => `${BASE_CURRENCY_CODE} ${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
  },
  {
    title: 'Approval', dataIndex: 'lineApprovalStatus', key: 'lineApprovalStatus', width: 110,
    render: (v: string) => {
      const cfg = LINE_APPROVAL_CONFIG[v] || { label: v, color: 'default' };
      return <Tag color={cfg.color}>{cfg.label}</Tag>;
    },
  },
];

// ── Create MR Modal (with dynamic line items) ──

interface CreateMRFormValues {
  departmentId: number;
  neededBy?: dayjs.Dayjs;
  urgency?: string;
  notes?: string;
}

interface LineItem {
  key: string;
  itemDescription: string;
  qty: number;
  unitPrice: number;
  partNumber?: string;
  vendorId?: number;
}

function CreateMRModal({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form] = Form.useForm<CreateMRFormValues>();
  const [lines, setLines] = useState<LineItem[]>([
    { key: '1', itemDescription: '', qty: 1, unitPrice: 0 },
  ]);
  const [saving, setSaving] = useState(false);

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { key: String(Date.now()), itemDescription: '', qty: 1, unitPrice: 0 },
    ]);
  };

  const removeLine = (key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
  };

  const updateLine = (key: string, field: keyof LineItem, value: unknown) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (lines.length === 0) {
        message.error('At least one line item is required');
        return;
      }
      for (const line of lines) {
        if (!line.itemDescription.trim()) {
          message.error('All lines must have an item description');
          return;
        }
        if (line.qty <= 0) {
          message.error('All lines must have qty > 0');
          return;
        }
      }

      setSaving(true);
      await createMR({
        departmentId: values.departmentId,
        neededBy: values.neededBy?.format('YYYY-MM-DD'),
        urgency: values.urgency,
        notes: values.notes,
        lines: lines.map((l) => ({
          itemDescription: l.itemDescription,
          qty: l.qty,
          unitPrice: l.unitPrice,
          partNumber: l.partNumber,
          vendorId: l.vendorId,
        })),
      });
      message.success('Material Request created');
      form.resetFields();
      setLines([{ key: '1', itemDescription: '', qty: 1, unitPrice: 0 }]);
      onClose();
      onCreated();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: { error: string } } }).response?.data?.error
        : 'Failed to create MR';
      message.error(msg || 'Failed to create MR');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setLines([{ key: '1', itemDescription: '', qty: 1, unitPrice: 0 }]);
    onClose();
  };

  const linesTotal = lines.reduce((sum, l) => sum + l.qty * l.unitPrice, 0);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Create Material Request"
      width={900}
      footer={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>Create MR</Button>
        </Space>
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ paddingTop: 16 }}>
        <Flex gap={16}>
          <Form.Item
            name="departmentId"
            label="Department ID"
            rules={[{ required: true, message: 'Department is required' }]}
            style={{ flex: 1 }}
          >
            <InputNumber style={{ width: '100%' }} min={1} placeholder="Enter department ID" />
          </Form.Item>
          <Form.Item name="neededBy" label="Needed By" style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="urgency" label="Urgency" style={{ flex: 1 }}>
            <Select
              placeholder="Select urgency"
              options={[
                { label: 'Low', value: 'low' },
                { label: 'Normal', value: 'normal' },
                { label: 'High', value: 'high' },
                { label: 'Critical', value: 'critical' },
              ]}
            />
          </Form.Item>
        </Flex>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="Optional notes..." />
        </Form.Item>
      </Form>

      <Divider>Line Items</Divider>

      <Table
        dataSource={lines}
        rowKey="key"
        size="small"
        pagination={false}
        columns={[
          {
            title: 'Item Description', dataIndex: 'itemDescription', key: 'itemDescription',
            render: (_, record) => (
              <Input
                value={record.itemDescription}
                onChange={(e) => updateLine(record.key, 'itemDescription', e.target.value)}
                placeholder="Item description"
              />
            ),
          },
          {
            title: 'Part #', dataIndex: 'partNumber', key: 'partNumber', width: 120,
            render: (_, record) => (
              <Input
                value={record.partNumber || ''}
                onChange={(e) => updateLine(record.key, 'partNumber', e.target.value)}
                placeholder="Part #"
              />
            ),
          },
          {
            title: 'Qty', dataIndex: 'qty', key: 'qty', width: 100,
            render: (_, record) => (
              <InputNumber
                value={record.qty}
                onChange={(v) => updateLine(record.key, 'qty', v ?? 0)}
                min={0.01}
                style={{ width: '100%' }}
              />
            ),
          },
          {
            title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', width: 130,
            render: (_, record) => (
              <InputNumber
                value={record.unitPrice}
                onChange={(v) => updateLine(record.key, 'unitPrice', v ?? 0)}
                min={0}
                precision={2}
                prefix={BASE_CURRENCY_CODE}
                style={{ width: '100%' }}
              />
            ),
          },
          {
            title: 'Total', key: 'total', width: 120, align: 'right' as const,
            render: (_, record) => (
              <Text strong>
                {BASE_CURRENCY_CODE} {(record.qty * record.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </Text>
            ),
          },
          {
            title: '', key: 'actions', width: 50,
            render: (_, record) => (
              lines.length > 1 ? (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeLine(record.key)}
                  size="small"
                />
              ) : null
            ),
          },
        ]}
        footer={() => (
          <Flex justify="space-between" align="center">
            <Button type="dashed" icon={<PlusOutlined />} onClick={addLine}>Add Line</Button>
            <Text strong>
              Total: {BASE_CURRENCY_CODE} {linesTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </Text>
          </Flex>
        )}
      />
    </Modal>
  );
}

// ── Main Page ──

export function MRListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<MRDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleFetch = useCallback(async (params: FetchParams) => {
    return fetchMRs(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const openDetail = async (id: number) => {
    setDrawerOpen(true);
    setDetailLoading(true);
    try {
      const data = await getMR(id);
      setDetail(data);
    } catch {
      message.error('Failed to load MR details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!detail) return;
    try {
      const updated = await submitMR(detail.id);
      setDetail(updated);
      message.success('MR submitted for approval');
      refresh();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: { error: string } } }).response?.data?.error
        : 'Failed to submit';
      message.error(msg || 'Failed to submit');
    }
  };

  const handleBulkApprove = async (status: 'approved' | 'rejected') => {
    if (!detail) return;
    const pendingLines = detail.lines.filter((l) => l.lineApprovalStatus === 'pending');
    if (pendingLines.length === 0) {
      message.info('No pending lines to approve');
      return;
    }
    try {
      const updated = await approveMRLines(
        detail.id,
        pendingLines.map((l) => ({ lineId: l.id, status })),
      );
      setDetail(updated);
      message.success(`All pending lines ${status}`);
      refresh();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: { error: string } } }).response?.data?.error
        : 'Failed to update';
      message.error(msg || 'Failed to update');
    }
  };

  const drawerActions = [];
  if (detail?.status === 'draft') {
    drawerActions.push({
      key: 'submit',
      label: 'Submit for Approval',
      icon: <SendOutlined />,
      onClick: handleSubmit,
    });
  }
  if (detail?.status === 'submitted') {
    drawerActions.push(
      {
        key: 'approve-all',
        label: 'Approve All Lines',
        icon: <CheckOutlined />,
        onClick: () => handleBulkApprove('approved'),
      },
      {
        key: 'reject-all',
        label: 'Reject All Lines',
        icon: <CloseOutlined />,
        onClick: () => handleBulkApprove('rejected'),
        danger: true,
      },
    );
  }

  const statusCfg = detail ? MR_STATUS_CONFIG[detail.status] || { label: detail.status, color: 'default' } : undefined;

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Material Requests</Title>
      </Flex>

      <DataTable<MRHeader>
        columns={columns}
        fetchData={handleFetch}
        rowKey="id"
        toolbar={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New MR
          </Button>
        }
        onRow={(record) => ({
          onClick: () => openDetail(record.id),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 1100 }}
      />

      <CreateMRModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />

      <DetailDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDetail(null); }}
        title={detail?.mrCode || ''}
        subtitle={detail ? `${detail.lineCount} lines` : undefined}
        status={statusCfg}
        loading={detailLoading}
        actions={drawerActions}
        width={800}
        tabs={detail ? [
          {
            key: 'details',
            label: 'Details',
            icon: <FileTextOutlined />,
            children: (
              <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="MR Code">{detail.mrCode}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={statusCfg?.color}>{statusCfg?.label}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Department ID">{detail.departmentId}</Descriptions.Item>
                <Descriptions.Item label="Urgency">
                  <Tag color={URGENCY_CONFIG[detail.urgency]?.color}>
                    {URGENCY_CONFIG[detail.urgency]?.label || detail.urgency}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Request Date">
                  {dayjs(detail.requestDate).format(DISPLAY_DATE_FORMAT)}
                </Descriptions.Item>
                <Descriptions.Item label="Needed By">
                  {detail.neededBy ? dayjs(detail.neededBy).format(DISPLAY_DATE_FORMAT) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  {BASE_CURRENCY_CODE} {Number(detail.mrTotal).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </Descriptions.Item>
                <Descriptions.Item label="PO Count">{detail.poCount ?? 0}</Descriptions.Item>
                {detail.notes && (
                  <Descriptions.Item label="Notes" span={2}>{detail.notes}</Descriptions.Item>
                )}
              </Descriptions>
            ),
          },
          {
            key: 'lines',
            label: `Lines (${detail.lines.length})`,
            icon: <UnorderedListOutlined />,
            children: (
              <Table
                dataSource={detail.lines}
                columns={lineColumns}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 800 }}
              />
            ),
          },
        ] : undefined}
      />
    </div>
  );
}
